import {
  Button,
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextArea,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import { forwardRef, useRef, useState } from "react"
import { useReactToPrint } from "react-to-print"

import {
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationStarted,
} from "./sharedTools"
import ToolPage from "./ToolPage"
import {
  generateTaskAnalysis,
  TaskAnalysisResponse as TaskAnalysis,
} from "../../networking/tools"
import { requireAuth } from "../../util/auth"

const TOOL_ID = "task_analysis_builder"

const difficultyColor = (level: string) => {
  switch (level.toLowerCase()) {
    case "easy":
      return JoonUIColor.semantic.success
    case "medium":
      return JoonUIColor.semantic.warning
    case "hard":
      return JoonUIColor.semantic.alert
    default:
      return JoonUIColor.text.secondary
  }
}

const TaskAnalysisBuilder = requireAuth(() => {
  const [task, setTask] = useState("")
  const [context, setContext] = useState("")
  const [taskAnalysis, setTaskAnalysis] = useState<TaskAnalysis | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const printableRef = useRef<HTMLDivElement>(null)
  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: taskAnalysis
      ? `Task Analysis - ${taskAnalysis.taskName}`
      : "Task Analysis",
  })

  const generate = async () => {
    setLoading(true)
    setError(null)
    const t0 = Date.now()
    trackGenerationStarted(TOOL_ID, {
      task_length: task.length,
      has_context: !!context,
    })
    try {
      const data = await generateTaskAnalysis(task, context)
      setTaskAnalysis(data)
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 })
    } catch (e: any) {
      setError("Failed to generate task analysis. Please try again.")
      trackGenerationFailed(TOOL_ID, { error_code: e.message })
    }
    setLoading(false)
  }

  return (
    <ToolPage
      toolId={TOOL_ID}
      title="Task Analysis Builder"
      description="Break down any skill into prerequisites, materials, and step-by-step instructions with difficulty ratings."
    >
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Task or skill to analyze
        </Typography>
        <TextInput
          name="Task or skill"
          value={task}
          onChange={(e) => setTask(e.target.value)}
          placeholder="e.g., Brushing teeth"
          fullWidth
        />
      </FlexBox>
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Additional context (optional)
        </Typography>
        <TextArea
          name="Additional context"
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder="e.g., The learner has fine motor difficulties"
          fullWidth
        />
      </FlexBox>
      <Button
        text="Generate task analysis"
        onClick={generate}
        disabled={loading || !task}
        fullWidth
      />
      {loading && (
        <FlexBox justify="center">
          <Loader size={24} color={JoonUIColor.semantic.primary} />
        </FlexBox>
      )}
      {error && (
        <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      {taskAnalysis && (
        <FlexBox
          direction="column"
          gap={SPACING.space4}
          style={{
            border: `1px solid ${JoonUIColor.border.default}`,
            borderRadius: SPACING.space2,
            padding: SPACING.space4,
          }}
        >
          <FlexBox justify="space-between" align="center" wrap={false}>
            <Typography variant="bodyBold" textAlign="left">
              Task analysis: {taskAnalysis.taskName}
            </Typography>
            <Button
              text="Export to PDF"
              buttonType="secondary"
              onClick={handlePrint}
            />
          </FlexBox>
          <TaskAnalysisView analysis={taskAnalysis} />
          <div style={{ display: "none" }}>
            <PrintableTaskAnalysis
              ref={printableRef}
              analysis={taskAnalysis}
            />
          </div>
        </FlexBox>
      )}
    </ToolPage>
  )
})

const TaskAnalysisView = ({ analysis }: { analysis: TaskAnalysis }) => (
  <FlexBox direction="column" gap={SPACING.space4}>
    <FlexBox direction="column" gap={SPACING.space1}>
      <Typography variant="bodyBold" textAlign="left">
        Materials
      </Typography>
      <ul style={{ marginTop: 0, paddingLeft: SPACING.space5 }}>
        {analysis.materials.map((m, i) => (
          <li key={i}>
            <Typography variant="bodySmall" textAlign="left">
              {m}
            </Typography>
          </li>
        ))}
      </ul>
    </FlexBox>
    <FlexBox direction="column" gap={SPACING.space1}>
      <Typography variant="bodyBold" textAlign="left">
        Prerequisites
      </Typography>
      <ul style={{ marginTop: 0, paddingLeft: SPACING.space5 }}>
        {analysis.prerequisites.map((p, i) => (
          <li key={i}>
            <Typography variant="bodySmall" textAlign="left">
              {p}
            </Typography>
          </li>
        ))}
      </ul>
    </FlexBox>
    <FlexBox direction="column" gap={SPACING.space2}>
      <Typography variant="bodyBold" textAlign="left">
        Steps
      </Typography>
      {analysis.steps.map((s, i) => (
        <FlexBox
          key={i}
          direction="column"
          gap={SPACING.space1}
          style={{
            border: `1px solid ${JoonUIColor.border.default}`,
            borderRadius: SPACING.space2,
            padding: SPACING.space3,
          }}
        >
          <FlexBox justify="space-between" align="center" wrap={false}>
            <Typography variant="bodySmall" textAlign="left">
              <strong>
                {s.stepNumber}. {s.description}
              </strong>
            </Typography>
            <span
              style={{
                color: difficultyColor(s.difficultyLevel),
                fontSize: 12,
                fontWeight: 600,
                border: `1px solid ${difficultyColor(s.difficultyLevel)}`,
                borderRadius: SPACING.space1,
                padding: `2px ${SPACING.space2}`,
                whiteSpace: "nowrap",
              }}
            >
              {s.difficultyLevel}
            </span>
          </FlexBox>
          <Typography variant="bodySmall" textAlign="left">
            <strong>Tip:</strong> {s.tips}
          </Typography>
        </FlexBox>
      ))}
    </FlexBox>
  </FlexBox>
)

const PrintableTaskAnalysis = forwardRef<
  HTMLDivElement,
  { analysis: TaskAnalysis }
>(({ analysis }, ref) => (
  <div ref={ref} style={{ padding: 24, fontFamily: "sans-serif" }}>
    <h2 style={{ marginTop: 0 }}>Task analysis: {analysis.taskName}</h2>
    <h3>Materials</h3>
    <ul>
      {analysis.materials.map((m, i) => (
        <li key={i}>{m}</li>
      ))}
    </ul>
    <h3>Prerequisites</h3>
    <ul>
      {analysis.prerequisites.map((p, i) => (
        <li key={i}>{p}</li>
      ))}
    </ul>
    <h3>Steps</h3>
    <ol>
      {analysis.steps.map((s, i) => (
        <li key={i} style={{ marginBottom: 12 }}>
          <strong>{s.description}</strong> <em>({s.difficultyLevel})</em>
          <div>Tip: {s.tips}</div>
        </li>
      ))}
    </ol>
  </div>
))
PrintableTaskAnalysis.displayName = "PrintableTaskAnalysis"

export default TaskAnalysisBuilder
